"use client";

import type React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  IconButton,
  Badge,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { FileText, FileUp } from "lucide-react";
import {
  smsKnowledgeBaseService,
  type SMSKnowledgeBase,
  type KnowledgeBaseText,
} from "../../sms-services/knowledgeBaseService";
import { AddTextModal } from "./AddTextModal";

interface KnowledgeBaseDetailsProps {
  knowledgeBase: SMSKnowledgeBase;
  onRefresh: (updatedKnowledgeBase?: SMSKnowledgeBase) => void;
  isPolling: boolean;
  setIsPolling: React.Dispatch<React.SetStateAction<boolean>>;
}

export const KnowledgeBaseDetails: React.FC<KnowledgeBaseDetailsProps> = ({
  knowledgeBase,
  onRefresh,
  isPolling,
  setIsPolling,
}) => {
  const MAX_FILES = 25;
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const [isLoading, setIsLoading] = useState(false);
  const [isAddingText, setIsAddingText] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();

  const handleAddText = async (text: KnowledgeBaseText) => {
    setIsLoading(true);
    try {
      await smsKnowledgeBaseService.addSources(
        knowledgeBase.knowledge_base_id,
        [text],
        undefined
      );
      toast({
        title: "Source added successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await smsKnowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
      setIsPolling(true);
    } catch (error: any) {
      toast({
        title: "Error adding text",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      setIsAddingText(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (e.target.files.length > MAX_FILES) {
      alert(`Please select at most ${MAX_FILES} files.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const filesArray = Array.from(e.target.files);

    for (const file of filesArray) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds 50MB limit.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const ext = file.name.toLowerCase();
      if (
        !ext.endsWith(".pdf") &&
        !ext.endsWith(".docx") &&
        !ext.endsWith(".txt")
      ) {
        alert(
          `File "${file.name}" is not a supported type. Only PDF, DOCX, and TXT are allowed.`
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    setIsLoading(true);
    try {
      const files = Array.from(e.target.files);

      await smsKnowledgeBaseService.addSources(
        knowledgeBase.knowledge_base_id,
        undefined,
        files
      );
      toast({
        title: "Source added successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await smsKnowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
    } catch (error: any) {
      console.error("Error adding files:", error);
      toast({
        title: "Error adding files",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteSource = async (sourceId: number) => {
    setIsLoading(true);
    try {
      await smsKnowledgeBaseService.removeSource(
        knowledgeBase.knowledge_base_id,
        sourceId
      );
      toast({
        title: "Source removed successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await smsKnowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
    } catch (error: any) {
      toast({
        title: "Error removing source",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    switch (knowledgeBase.status) {
      case "in_progress":
        return (
          <Badge
            colorScheme="yellow"
            backgroundColor={"yellow.30"}
            color={"yellow.400"}
          >
            Processing
          </Badge>
        );
      case "complete":
        return (
          <Badge
            colorScheme="green"
            backgroundColor={"green.30"}
            color={"green.400"}
          >
            Ready to use
          </Badge>
        );
      case "error":
        return (
          <Badge colorScheme="red" backgroundColor={"red.30"} color={"red.400"}>
            Error
          </Badge>
        );
      default:
        return null;
    }
  };

  const getSourceIcon = (type: string) => {
    return <FileText size={16} />;
  };

  const pollKnowledgeBase = useCallback(async () => {
    try {
      const updatedKnowledgeBase = await smsKnowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);

      if (
        updatedKnowledgeBase.status === "complete" ||
        updatedKnowledgeBase.status === "error"
      ) {
        setIsPolling(false);
      }
    } catch (error) {
      console.error("Error polling knowledge base status:", error);
      setIsPolling(false);
    }
  }, [knowledgeBase.knowledge_base_id, onRefresh, setIsPolling]);

  useEffect(() => {
    if (isPolling || knowledgeBase.status === "in_progress") {
      const interval = setInterval(() => {
        pollKnowledgeBase();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isPolling, knowledgeBase.status, pollKnowledgeBase]);

  return (
    <Box p={4}>
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between">
          <Heading size="md" color={"gray.800"}>
            {knowledgeBase.knowledge_base_name}
          </Heading>
          {getStatusBadge()}
        </HStack>

        <HStack spacing={4}>
          <Button
            leftIcon={<FileUp size={16} />}
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            isLoading={isLoading}
            color={"gray.700"}
            borderColor={"gray.300"}
            _hover={{ bg: "gray.100" }}
          >
            Upload Files
          </Button>
          <Button
            leftIcon={<FileText size={16} />}
            size="sm"
            variant="outline"
            onClick={() => setIsAddingText(true)}
            isLoading={isLoading}
            color={"gray.700"}
            borderColor={"gray.300"}
            _hover={{ bg: "gray.100" }}
          >
            Add Text
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
            accept=".pdf,.docx,.txt"
          />
        </HStack>

        <Divider borderColor={"gray.200"} />

        <VStack spacing={4} align="stretch">
          <Heading size="sm" color={"gray.800"}>
            Sources ({knowledgeBase.source_count || 0})
          </Heading>

          {!knowledgeBase.sources || knowledgeBase.sources.length === 0 ? (
            <Text color={"gray.600"}>No sources added yet</Text>
          ) : (
            knowledgeBase.sources.map((source) => (
              <HStack
                key={source.id}
                p={3}
                bg={"gray.100"}
                borderRadius="md"
                justify="space-between"
              >
                <HStack>
                  {getSourceIcon(source.source_type)}
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" color={"gray.800"} fontSize="sm">
                      {source.original_filename}
                    </Text>
                    <Text fontSize="xs" color={"gray.600"}>
                      {source.source_type.toUpperCase()} •{" "}
                      {source.byte_size
                        ? (source.byte_size / 1024).toFixed(1)
                        : "0"}{" "}
                      KB
                    </Text>
                  </VStack>
                </HStack>
                <IconButton
                  aria-label="Delete source"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteSource(source.id)}
                  isDisabled={isLoading}
                  color={"red.500"}
                  _hover={{ bg: "red.50" }}
                />
              </HStack>
            ))
          )}
        </VStack>
      </VStack>

      <AddTextModal
        isOpen={isAddingText}
        onClose={() => setIsAddingText(false)}
        onSave={handleAddText}
      />
    </Box>
  );
};
